/**
 * Business Impact utilities.
 *
 * - `hasNonEmptyBIAnswer` — returns true when at least one BI question id has
 *   a non-empty answer string.
 * - `shouldFireBusinessImpact` — gates the auto-generation call. Per spec
 *   §2.6.3 / §3.3, the BI generate endpoint is fired only when:
 *     1. At least one BI answer is non-empty.
 *     2. `summaryShort` is not already set.
 *     3. `editedAt` is not set (user hasn't hand-typed a summary).
 * - `countWords` — whitespace-separated non-empty token count. Hyphenated
 *   terms (e.g., "mission-critical") count as **one** word, per spec §2.7.1.
 */

import type { FlowMetadata } from '../types/flow';

export function hasNonEmptyBIAnswer(
  answers: Record<string, string>,
  biQuestionIds: Set<string>
): boolean {
  let found = false;
  biQuestionIds.forEach((id) => {
    if (!found && (answers[id] ?? '').trim() !== '') {
      found = true;
    }
  });
  return found;
}

export function shouldFireBusinessImpact(
  flow: FlowMetadata,
  hasNonEmpty: boolean
): boolean {
  if (['services', 'complex_services'].includes(flow.workflow_type || '')) {
    const impact = flow.businessImpact;
    if (impact?.stale || ['stale', 'pending', 'failed'].includes(impact?.status || '')) return true;
    if (impact?.status === 'ready' || impact?.status === 'empty') return false;
  }
  if (!hasNonEmpty) return false;
  if (flow.businessImpact?.summaryShort) return false;
  if (flow.businessImpact?.editedAt) return false;
  return true;
}

/**
 * Count whitespace-separated non-empty tokens. Hyphenated terms count as one
 * word (the splitter only splits on whitespace, not on hyphens).
 */
export function countWords(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length;
}
